/* Copyright (c) 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Chart as ChartJS, ArcElement, Tooltip } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { If, Then, Else, When } from "react-if";
import { Spinner, Text, theme } from "@nordcloud/gnui";
import { isNotNil } from "~/tools";
import { StatusBarWrap, StatusBarItem, DoughnutChartTextWrap } from "./styles";
import { StatusChartItem } from "./types";

ChartJS.register(ArcElement, Tooltip);

const DOUGHNUT_CHART_OPTIONS = {
  cutout: "80%",
};

const EMPTY_DOUGHNUT_COLOR = theme.colors.lights[3];

const DOUGHNUT_CHART_OPTIONS_NO_TOOLTIP = {
  ...DOUGHNUT_CHART_OPTIONS,
  plugins: { tooltip: { enabled: false } },
};

type Props = {
  items: StatusChartItem[];
  loading?: boolean;
  chartLabel?: string;
  isDonutChart?: boolean;
};

export function StatusChart({
  items,
  loading = false,
  chartLabel,
  isDonutChart = true,
}: Props) {
  const total = items.reduce((acc, { count }) => acc + count, 0);

  if (isDonutChart) {
    const doughnutData = mapToDoughnutData(items, total);
    const doughnutOptions =
      total === 0 ? DOUGHNUT_CHART_OPTIONS_NO_TOOLTIP : DOUGHNUT_CHART_OPTIONS;

    return (
      <div css={{ position: "relative" }}>
        <Doughnut
          data={doughnutData}
          options={doughnutOptions}
          style={{
            display: "inline-block",
          }}
        />
        <DoughnutChartTextWrap>
          <If condition={loading}>
            <Then>
              <Spinner />
            </Then>
            <Else>
              <Text size="xl" mb={0}>
                {total}
              </Text>
              <When condition={isNotNil(chartLabel)}>
                <Text size="sm" color={theme.color.text.text01} mb={0}>
                  {chartLabel}
                </Text>
              </When>
            </Else>
          </If>
        </DoughnutChartTextWrap>
      </div>
    );
  }

  return (
    <StatusBarWrap>
      <If condition={!loading && total !== 0}>
        <Then>
          {items
            .filter((item) => item.count > 0)
            .map((item) => (
              <StatusBarItem
                hasAnimation
                key={item.label}
                width={`${(item.count / total) * 100}%`}
                background={item.color}
              />
            ))}
        </Then>
        <Else>
          <StatusBarItem
            width="100%"
            background={theme.color.background.body}
          />
        </Else>
      </If>
    </StatusBarWrap>
  );
}

const mapToDoughnutData = (input: StatusChartItem[], total: number) => {
  const { labels, values, colors } =
    total === 0
      ? { labels: ["No data"], values: [1], colors: [EMPTY_DOUGHNUT_COLOR] }
      : input.reduce(
          (acc, { label, count, color }) => ({
            labels: [...acc.labels, label],
            values: [...acc.values, count],
            colors: [...acc.colors, color],
          }),
          { labels: [], values: [], colors: [] } as {
            labels: string[];
            values: number[];
            colors: string[];
          }
        );

  return {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: colors,
        borderColor: colors,
        borderWidth: 1,
      },
    ],
  };
};
